import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useSnackbar } from 'notistack'
import Button from '@mui/material/Button'

const CHECK_INTERVAL_MS = 5 * 60 * 1000

function getLoadedBuildId(): string | undefined {
  return (window as unknown as { __NEXT_DATA__?: { buildId?: string } })
    .__NEXT_DATA__?.buildId
}

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    // Revalidates via ETag, so an unchanged page costs only a 304 with no body.
    const res = await fetch('/', { cache: 'no-cache' })
    if (!res.ok) return null
    const html = await res.text()
    return html.match(/"buildId":"([^"]+)"/)?.[1] ?? null
  } catch {
    return null
  }
}

export default function VersionChecker() {
  const router = useRouter()
  const { enqueueSnackbar } = useSnackbar()
  const updateAvailable = useRef(false)

  useEffect(() => {
    const loadedBuildId = getLoadedBuildId()
    if (!loadedBuildId || loadedBuildId === 'development') return

    const check = async () => {
      if (updateAvailable.current) return
      const deployedBuildId = await fetchDeployedBuildId()
      if (!deployedBuildId || deployedBuildId === loadedBuildId) return

      updateAvailable.current = true
      enqueueSnackbar('A new version of nPOS is available.', {
        variant: 'info',
        persist: true,
        preventDuplicate: true,
        action: () => (
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        ),
      })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }

    check()
    const interval = window.setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enqueueSnackbar])

  // Page state is already discarded on navigation, so reloading here can't lose in-progress work.
  useEffect(() => {
    const onRouteChangeComplete = () => {
      if (updateAvailable.current) window.location.reload()
    }
    router.events.on('routeChangeComplete', onRouteChangeComplete)
    return () => router.events.off('routeChangeComplete', onRouteChangeComplete)
  }, [router.events])

  return null
}
